import { axiosClient } from "@/plugins/axios"
import { v4 as uuid } from "uuid"

class TransferFile {
  constructor(file = null) {
    this.file = file
    this.uid = uuid()
    this.oftFileName = null
    this.oftUid = null
    this.uploadLink = null
    this.uploaded = false
    this.uploadProgress = 0

    this._objectUrl = null
  }

  getObjectUrl() {
    if (!this._objectUrl && this.file) {
      this._objectUrl = URL.createObjectURL(this.file)
    }
    return this._objectUrl
  }

  revokeObjectUrl() {
    if (this._objectUrl) {
      URL.revokeObjectURL(this._objectUrl)
      this._objectUrl = null
    }
  }
}

class OftAPI {
  constructor() {
    this.country = null
    this.fileProgress = []
    this.totalFileSize = 0
    this.progressCallback = null
  }

  setCountry(country) {
    this.country = country
  }

  prepareFiles(files) {
    return files.map((file) => new TransferFile(file))
  }

  async uploadFiles(files, progressCallback = null) {
    if (!files) return

    const isArray = Array.isArray(files)
    if (!isArray) files = [files]

    if (files.length === 0) return

    // Initialize progress tracking
    if (progressCallback !== null) {
      this._initializeProgress(files)
      this.progressCallback = progressCallback
    }

    const oftResponse = await this._sendToOft(files).catch(() => {
      return
    })

    // Update file metadata with oft response
    files.forEach((file, index) => {
      file.oftFileName = oftResponse[index].fileName
      file.uploadLink = oftResponse[index].oneTimeUploadLink
      file.oftUid = oftResponse[index].uid
    })

    // Prepare upload promises. Pass in progress update method.
    const uploadPromises = files.map((file, index) => {
      if (!file.uploaded) {
        return this._uploadToGcs(
          file.file,
          file.uploadLink,
          index,
          files,
          this._updateProgress.bind(this)
        ).then(() => {
          file.uploaded = true
        })
      }
    })

    await Promise.allSettled(uploadPromises)

    return isArray ? files : files[0]
  }

  async getFileUrls(files) {
    if (!files) return

    const isArray = Array.isArray(files)
    if (!isArray) files = [files]

    if (files.length === 0) return

    const oftFileUids = files.map((file) => file.oftUid)

    let response
    const queryString = oftFileUids.map((uid) => `uid=${uid}`).join("&")

    try {
      response = await axiosClient.get(`/proxy/files/?${queryString}`)
    } catch (error) {
      console.error(
        `Error fetching file URLs from OFT with params: ${queryString}`,
        error
      )
      throw error
    }

    files.forEach((file) => {
      const oftFile = response.data.find((item) => item.uid === file.oftUid)
      if (oftFile) {
        file.url = oftFile.oneTimeDownloadLink
        file.oftFileName = oftFile.fileName
      }
    })

    return isArray ? files : files[0]
  }

  async _sendToOft(files) {
    const payload = files.map((file) => {
      if (!file.uploaded)
        return {
          fileName: file.file.name
        }
    })
    try {
      const response = await axiosClient.post(
        "/proxy/files/" + (this.country ? `${this.country}/` : ""),
        payload
      )
      return response.data
    } catch (error) {
      console.error("Failed to add files to OFT:", error)
      throw error
    }
  }

  async _uploadToGcs(file, url, index, files, updateProgress) {
    try {
      const response = await axiosClient.put(url, file, {
        headers: {
          "Content-Type": file.type || "application/octet-stream",
          Authorization: undefined
        },
        onUploadProgress: (progressEvent) => {
          if (this.progressCallback !== null)
            updateProgress(index, progressEvent.loaded, files)
        }
      })
      return response
    } catch (error) {
      console.error(`Error uploading file ${file.name}:`, error)
      throw error
    }
  }

  _initializeProgress(files) {
    this.totalFileSize = files.reduce((sum, file) => {
      return sum + (!file.uploaded ? file.file.size : 0)
    }, 0)
  }

  _resetProgress() {
    this.fileProgress = []
    this.totalFileSize = 0
    this.progressCallback = null
  }

  _updateProgress(index, loaded, files) {
    files[index].uploadProgress = loaded

    const totalUploaded = files.reduce(
      (sum, file) => sum + file.uploadProgress,
      0
    )

    const totalProgress = Math.min(
      Math.round((totalUploaded / this.totalFileSize) * 100),
      100
    )

    this.progressCallback(totalProgress)
  }
}

var oftClient = new OftAPI()

export default oftClient
